import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  GFPanelLeft,
  GFSearch,
  GFBell,
  GFSparkles,
  GFGlobe,
  GFLogout,
  GFMoon,
  GFSun,
  GFChevronRight,
} from '../icons/GFIcons';
import { SyncStatusIndicator } from '../../offlinesync';

interface HeaderProps {
  onMenuClick: () => void;
  onToggleSidebar?: () => void;
  onOpenSearch?: () => void;
  onToggleAI?: () => void;
  aiOpen?: boolean;
  onToggleNotifs?: () => void;
  onToggleTheme?: () => void;
  isDark?: boolean;
}

function buildBreadcrumbs(pathname: string, t: any): string[] {
  // Map known routes to crumb chains
  const map: Record<string, string[]> = {
    '/': ['Espace de travail', t.nav.dashboard],
    '/clients': ['Espace de travail', t.nav.clients],
    '/clients/new': ['Espace de travail', t.nav.clients, 'Nouveau'],
    '/articles': ['Espace de travail', t.nav.articles],
    '/articles/new': ['Espace de travail', t.nav.articles, 'Nouveau'],
    '/documents/factures': ['Documents', t.nav.factures],
    '/documents/devis': ['Documents', t.nav.devis],
    '/documents/avoirs': ['Documents', t.nav.avoirs],
    '/documents/bons-livraison': ['Documents', t.nav.bonsLivraison],
    '/paiements': ['Opérations', t.nav.paiements],
    '/stock/mouvements': ['Opérations', 'Stock'],
    '/rapports/tva': ['Finance & conformité', t.nav.rapportTva],
    '/rapports/etat-annuel': ['Finance & conformité', t.nav.etatAnnuel],
    '/rapports/export': ['Finance & conformité', 'Export comptable'],
    '/parametres': ['Plateforme', t.nav.parametres],
  };

  if (map[pathname]) return map[pathname];

  // Best-effort prefix match
  const prefixed = Object.keys(map)
    .filter((p) => p !== '/' && pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  if (prefixed) {
    const segments = pathname.slice(prefixed.length).split('/').filter(Boolean);
    return [...map[prefixed], ...segments.map((s) => decodeURIComponent(s))];
  }
  return ['Espace de travail'];
}

export function Header({
  onMenuClick,
  onToggleSidebar,
  onOpenSearch,
  onToggleAI,
  aiOpen,
  onToggleNotifs,
  onToggleTheme,
  isDark,
}: HeaderProps) {
  const { language, toggleLanguage, t } = useLanguage();
  const { logout } = useAuth();
  const location = useLocation();

  const crumbs = useMemo(() => buildBreadcrumbs(location.pathname, t), [location.pathname, t]);

  return (
    <header className="topbar">
      <button
        type="button"
        className="icon-btn"
        onClick={onToggleSidebar || onMenuClick}
        title="Basculer la sidebar"
      >
        <GFPanelLeft size={16} />
      </button>

      <div className="topbar-breadcrumb">
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <GFChevronRight size={12} className="crumb-sep" />}
            <span className={i === crumbs.length - 1 ? 'crumb-current' : ''}>{c}</span>
          </React.Fragment>
        ))}
      </div>

      <div className="topbar-spacer" />

      <button type="button" className="search-trigger" onClick={onOpenSearch}>
        <GFSearch size={14} />
        <span>Recherche, action, IA…</span>
        <span className="kbd">⌘K</span>
      </button>

      <div className="divider-v" />

      <SyncStatusIndicator />

      <button
        type="button"
        className="icon-btn"
        onClick={toggleLanguage}
        title={language === 'fr' ? 'Passer en arabe' : 'التبديل إلى الفرنسية'}
      >
        <GFGlobe size={16} />
      </button>

      {onToggleTheme && (
        <button
          type="button"
          className="icon-btn"
          onClick={onToggleTheme}
          title={isDark ? 'Mode clair' : 'Mode sombre'}
        >
          {isDark ? <GFSun size={16} /> : <GFMoon size={16} />}
        </button>
      )}

      <button type="button" className="icon-btn" title="Activité" onClick={onToggleNotifs}>
        <GFBell size={16} />
        <span className="dot" />
      </button>

      <button
        type="button"
        className={`icon-btn ${aiOpen ? 'active' : ''}`}
        title="Assistant IA"
        onClick={onToggleAI}
        style={aiOpen ? { color: 'var(--brand-green)' } : undefined}
      >
        <GFSparkles size={16} />
      </button>

      <button
        type="button"
        className="icon-btn"
        onClick={() => logout()}
        title="Se déconnecter"
      >
        <GFLogout size={16} />
      </button>
    </header>
  );
}

export default Header;
